<script>
import responsiveMixin from '@/mixins/responsiveMixin';
import { helpers as notificationHelpers } from '@/store/modules/notifications';
import { helpers as researcherHelpers } from '@/store/modules/researcherSignup';
import * as types from '@/store/types';
import Api from '@/utils/api';
import * as Segment from '@/utils/segment';
import { exists } from '@/utils/validationRules';
import { MarketingPricingPlanType } from '../../../../lib/helpers/enums';

const sourceOpts = [
  'Friend or Colleague Referred Me',
  'Former Employer',
  'Search Engine (e.g. Google)',
  'Article or Blog Post',
  'Respondent Contacted Me',
  'Other',
];

export default {
  mixins: [responsiveMixin],
  data: () => ({
    formIsValid: false,
    isSubmitting: false,
    jobTitle: '',
    rules: {
      exists: () => true,
      validSource: () => true,
    },
    source: '',
    sourceOpts,
  }),
  computed: {
    ...researcherHelpers.mapState({
      organization: 'organization',
      team: 'team',
      userId: (state) => state.user.id,
      email: (state) => state.user.email,
    }),
  },
  methods: {
    ...notificationHelpers.mapActions({ showSnackbar: types.SHOW_SNACKBAR }),
    ...researcherHelpers.mapMutations({ setFields: types.SET_FIELDS }),
    setRules() {
      this.rules = {
        exists,
        validSource: (v) => sourceOpts.includes(v) || 'Required',
      };
    },
    async completeOnboarding() {
      this.setRules();
      this.$nextTick(async () => {
        await this.$refs.form.validate();
        if (!this.formIsValid) return;
        this.isSubmitting = true;

        try {
          const { jobTitle, source } = this;
          const params = {
            jobTitle,
            source,
            userId: this.userId,
          };

          const { data: { success } } = await Api.post('/signup/complete-onboarding', params);
          const { data: { success: success2, response: teams } } = await Api.get(`/dashboard/teams`);
          if (!success || !success2) {
            throw new Error();
          }

          const teamId = teams[0].id;
          const { data: teamPlanSummary } = await Api.get(`/api/v4/pricing/teams/${teamId}/summary`);

          this.setFields({ jobTitle, source });
          this.trackSegmentSignup();

          let planType = window.sessionStorage.getItem('planType');
          const baseRoute = `/dashboard/organizations/${this.organization.id}/teams/${teamId}`

          if (teamPlanSummary.plan?.pricingVersion === 3) {
            const planTypeMap = {
              [MarketingPricingPlanType.PAYG]: MarketingPricingPlanType.PAYG_V3,
              [MarketingPricingPlanType.BASIC]: MarketingPricingPlanType.BASIC_V3,
              [MarketingPricingPlanType.ADVANCED]: MarketingPricingPlanType.ADVANCED_V3,
            }
            planType = planTypeMap[planType];

            let redirectUrl;
            if (planType === MarketingPricingPlanType.PAYG_V3) {
              redirectUrl = baseRoute;
            } else if (planType) {
              redirectUrl = `${baseRoute}/subscription/purchase-plan?planType=${planType}&reason=new`
            } else {
              redirectUrl = `${baseRoute}/subscription/select-plan?reason=new`;
            }

            window.location.href = redirectUrl;
          } else {
            // TODO Remove when feature flag is removed
            // @see https://respondent.atlassian.net/browse/XP-3268
            let redirectUrl;
            if (!planType || planType === MarketingPricingPlanType.PAYG) {
              redirectUrl = `${baseRoute}/subscription/choose`;
            } else {
              const participantType = window.sessionStorage.getItem('participantType');
              redirectUrl = `${baseRoute}/subscription/purchase?plan-type=${planType}&participant-type=${participantType}`;
            }

            window.location.href = redirectUrl;
          }

          return;
        } catch (error) {
          this.isSubmitting = false;

          this.showSnackbar({
            message:
              'Something went wrong updating your account. Please try again later.',
          });
        }
      });
    },
    trackSegmentSignup() {
      Segment.trackResearcher('Researcher_Signup_Completed', {
        email: this.email,
        jobTitle: this.jobTitle,
        source: this.source,
      });
      Segment.trackResearcher('Participant_Onboarding_Complete');
    },
  },
};
</script>

<template>
  <v-row class="pt-0">
    <v-col cols="12" md="7">
      <h1 :class="responsiveClasses({
        tablet: 'display-3 mb-4',
        desktop: 'display-4 mb-5',
      })
        ">
        Tell us a little about yourself
      </h1>
      <v-form v-model="formIsValid" method="POST" @submit.prevent="completeOnboarding" ref="form">
        <base-input v-model="jobTitle" label="What's your job title?" placeholder="Product Designer" type="text"
          :rules="[rules.exists]" />

        <base-select v-model="source" label="How did you hear about us?" :options="sourceOpts"
          placeholder="Select an option" :rules="[rules.validSource]" />

        <v-btn color="primary" :loading="isSubmitting" type="submit" :min-width="261" :block="isMobileOrTablet">
          Next
        </v-btn>
      </v-form>
    </v-col>
  </v-row>
</template>
